<script lang="ts">
	import AppContainer from '../../components/AppContainer.svelte';
	import Text from '../../components/Text.svelte';
	import Flex from 'svelte-flex';
	import Button from '@smui/button/styled';
	import LookalikeGraph from '../../components/Lookalike/LookalikeGraph.svelte';
	import { getClient, query } from 'svelte-apollo';
	import { getLookalikeModelRowInfoQuery } from '../../graphql/queries/lookalike';
	import LoadingOverlay from '../../components/LoadingOverlay.svelte';
	import { onMount } from 'svelte';
	import type { ApolloQueryResult } from 'apollo-boost';
	import type { LookalikeDomain, LookalikeModelRow } from 'Lookalike';
	import SimpleTable from '../../components/SimpleTable.svelte';

	export let id: string;

	const client = getClient();

	let row: LookalikeModelRow;
	let domains: LookalikeDomain[];
	let filteredDomains: LookalikeDomain[];
	let xAxisValues: number[];
	let yAxisValues: number[];
	let selectedRatio: number;
	let selectedPercent: number;
  let selectedLang = "ALL";
  const regexExpCz = /\.cz/g;
  const regexExpSk = /\.sk/g;

	let columns = [
		{
			key: 'score',
			title: 'Score',
			value: (v: LookalikeDomain) => {
				if (v.Bucketized_score !== undefined) {
					return v.Bucketized_score.toFixed(2);
				} else {
					return '';
				}
			},
			sortable: true
		},
		{
			key: 'domain',
			title: 'Domain',
			value: (v: LookalikeDomain) => v.domain
		}
	];

	onMount(async () => {
		const getLookalikeModelRowInfoOp = query(client, {
			query: getLookalikeModelRowInfoQuery,
			variables: { rowId: id }
		});
		getLookalikeModelRowInfoOp.subscribe(async (promise) => {
			await (promise as Promise<ApolloQueryResult<any>>).then((data) => {
				row = JSON.parse(data.data.lookalikeModelRowInfo).row;
				domains = JSON.parse(JSON.parse(data.data.lookalikeModelRowInfo).domains).WL;
				filteredDomains = domains;
				xAxisValues = JSON.parse(JSON.parse(data.data.lookalikeModelRowInfo).domains).x_axis;
				yAxisValues = JSON.parse(JSON.parse(data.data.lookalikeModelRowInfo).domains).y_axis.map(
					(value: number) => {
						return value * 100;
					}
				);
			});
		});
	});

	$: {
		if (selectedPercent !== undefined) {
			filteredDomains = domains.filter((domain) => {
				return domain.ratio_all_cumsum * 100 <= selectedPercent;
			});
		} else {
			filteredDomains = undefined;
		}
	}

	const downloadCsv = function (content: string, fileName: string, mimeType: string) {
		let a = document.createElement('a');
		mimeType = mimeType || 'application/octet-stream';

		if (URL && 'download' in a) {
			a.href = URL.createObjectURL(
				new Blob([content], {
					type: mimeType
				})
			);
			a.setAttribute('download', fileName);
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		} else {
			location.href = 'data:application/octet-stream,' + encodeURIComponent(content);
		}
	};

	function generateWhitelist() {
    let wholeWhitelist = filteredDomains;

    if (selectedLang === "CZ") {
      wholeWhitelist = filteredDomains.filter((el) => {
        if (el.domain.match(regexExpCz) !== null && el.domain.match(regexExpCz)[0] === ".cz") {
          return true;
        } else {
          return false;
        }
      });
    }

    if (selectedLang === "SK") {
     wholeWhitelist = filteredDomains.filter((el) => {
        if (el.domain.match(regexExpSk) !== null && el.domain.match(regexExpSk)[0] === ".sk") {
          return true;
        } else {
          return false;
        }
      });
    }

		const fixedWhitelist = wholeWhitelist.map((domain) => {
			return [domain.domain, domain.Bucketized_score.toFixed(2)];
		});

		let csvContent = '';
		fixedWhitelist.forEach((infoArray, index) => {
			const dataString = infoArray.join(';');
			csvContent += index < fixedWhitelist.length ? dataString + '\n' : dataString;
		});
		downloadCsv(csvContent, 'whitelist.csv', 'text/csv;encoding:utf-8');
	}
</script>

<AppContainer>
	{#if row === undefined || domains === undefined}
		<LoadingOverlay title="Loading..." spinner />
	{:else}
		<Text gutter type="headline4">Lookalike targeting detail</Text>
		<div class="head-part">
			<Flex justify="between">
				<Text>
					{#if row.type === 'TP'}
						<strong>Trackingpoint:</strong>
						{row.target_name}, ID {row.target_id}<br />
						<strong>Days before trackingpoint:</strong>
						{row.days}
					{:else}
						<strong>DMP Segment name:</strong>
						{row.segment_name}
					{/if}
				</Text>
				{#if typeof filteredDomains !== 'undefined' && filteredDomains.length > 0}
					<Button variant="raised" on:click={() => generateWhitelist()}>DOWNLOAD</Button>
				{/if}
			</Flex>
		</div>
		<Text
			>Please select desired percent of traffic you want to target (click on dot in graph below):
		</Text>
		<LookalikeGraph
			xAxis={xAxisValues}
			yAxis={yAxisValues}
			bind:percent={selectedPercent}
			bind:ratio={selectedRatio}
		/>
		<div class="table-wrapper">
			{#if typeof filteredDomains !== 'undefined'}
				{#if filteredDomains.length > 0}
          <div class="row">
            <div>
              <Button
                variant={selectedLang === 'ALL' ? 'raised' : 'outlined'}
                on:click={() => {selectedLang = "ALL"}}
              >
                ALL
              </Button>
              <Button
                variant={selectedLang === 'CZ' ? 'raised' : 'outlined'}
                on:click={() => {selectedLang = "CZ"}}
              >
                CZ
              </Button>
              <Button
                variant={selectedLang === 'SK' ? 'raised' : 'outlined'}
                on:click={() => {selectedLang = "SK"}}
              >
                SK
              </Button>
            </div>
          </div>
          {#if selectedLang === "ALL"}
            <br /><Text>Number of domains: <strong>{filteredDomains.length}</strong></Text><br />
					<SimpleTable {columns} rows={filteredDomains} />
          {/if}
          {#if selectedLang === "CZ"}
            <br /><Text>Number of domains: <strong>{filteredDomains.filter((el) => {
              if (el.domain.match(regexExpCz) !== null && el.domain.match(regexExpCz)[0] === ".cz") {
                return true;
              } else {
                return false;
              }
          }).length}</strong></Text><br />
          <SimpleTable {columns} rows={filteredDomains.filter((el) => {
              if (el.domain.match(regexExpCz) !== null && el.domain.match(regexExpCz)[0] === ".cz") {
                return true;
              } else {
                return false;
              }
          })} />
          {/if}
          {#if selectedLang === "SK"}
            <br /><Text>Number of domains: <strong>{filteredDomains.filter((el) => {
            if (el.domain.match(regexExpSk) !== null && el.domain.match(regexExpSk)[0] === ".sk") {
              return true;
            } else {
              return false;
            }
          }).length}</strong></Text><br />
            <SimpleTable {columns} rows={filteredDomains.filter((el) => {
              if (el.domain.match(regexExpSk) !== null && el.domain.match(regexExpSk)[0] === ".sk") {
                return true;
              } else {
                return false;
              }
          })} />
          {/if}
				{:else}
					<Text>There are no domains for this settings.</Text>
				{/if}
			{/if}
		</div>
	{/if}
</AppContainer>

<style>
	.head-part {
		margin-top: 25px;
		margin-bottom: 30px;
	}

	.table-wrapper {
		margin-top: 25px;
	}
</style>
